import _regeneratorRuntime from "@babel/runtime/regenerator";

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import { logger, timestampInSeconds, uuid4 } from '@sentry/utils';
import { WINDOW } from '../helpers.js';
import { shouldProfileTransaction, isAutomatedPageLoadTransaction, startJSSelfProfile, MAX_PROFILE_DURATION_MS, addProfileToGlobalCache } from './utils.js';
/**
 * Safety wrapper for startTransaction for the unlikely case that transaction starts before tracing is imported -
 * if that happens we want to avoid throwing an error from profiling code.
 * see https://github.com/getsentry/sentry-javascript/issues/4731.
 *
 * @experimental
 */

function onProfilingStartRouteTransaction(transaction) {
  if (!transaction) {
    if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
      logger.log('[Profiling] Transaction is undefined, skipping profiling');
    }

    return transaction;
  }

  if (shouldProfileTransaction(transaction)) {
    return startProfileForTransaction(transaction);
  }

  return transaction;
}
/**
 * Wraps startTransaction and stopTransaction with profiling related logic.
 * startProfileForTransaction is called after the call to startTransaction in order to avoid our own code from
 * being profiled. Because of that same reason, stopProfiling is called before the call to stopTransaction.
 */


function startProfileForTransaction(transaction) {
  // Start the profiler and get the profiler instance.
  var startTimestamp;

  if (isAutomatedPageLoadTransaction(transaction)) {
    startTimestamp = timestampInSeconds() * 1000;
  }

  var profiler = startJSSelfProfile(); // We failed to construct the profiler, fallback to original transaction.
  // No need to log anything as this has already been logged in startProfile.

  if (!profiler) {
    return transaction;
  }

  if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
    logger.log("[Profiling] started profiling transaction: ".concat(transaction.name || transaction.description));
  } // We create "unique" transaction names to avoid concurrent transactions with same names
  // from being ignored by the profiler. From here on, only this transaction name should be used when
  // calling the profiler methods. Note: we log the original name to the user to avoid confusion.


  var profileId = uuid4();
  /**
   * Idempotent handler for profile stop
   */

  function onProfileHandler() {
    return _onProfileHandler.apply(this, arguments);
  } // Enqueue a timeout to prevent profiles from running over max duration.


  function _onProfileHandler() {
    _onProfileHandler = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (transaction) {
                _context.next = 2;
                break;
              }

              return _context.abrupt("return", null);

            case 2:
              if (profiler) {
                _context.next = 4;
                break;
              }

              return _context.abrupt("return", null);

            case 4:
              return _context.abrupt("return", profiler.stop().then(function (profile) {
                if (maxDurationTimeoutID) {
                  WINDOW.clearTimeout(maxDurationTimeoutID);
                  maxDurationTimeoutID = undefined;
                }

                if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
                  logger.log("[Profiling] stopped profiling of transaction: ".concat(transaction.name || transaction.description));
                } // In case of an overlapping transaction, stopProfiling may return null and silently ignore the overlapping profile.


                if (!profile) {
                  if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
                    logger.log("[Profiling] profiler returned null profile for: ".concat(transaction.name || transaction.description), 'this may indicate an overlapping transaction or a call to stopProfiling with a profile title that was never started');
                  }

                  return null;
                }

                addProfileToGlobalCache(profileId, profile);
                return null;
              }).catch(function (error) {
                if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
                  logger.log('[Profiling] error while stopping profiler:', error);
                }

                return null;
              }));

            case 5:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return _onProfileHandler.apply(this, arguments);
  }

  var maxDurationTimeoutID = WINDOW.setTimeout(function () {
    if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
      logger.log('[Profiling] max profile duration elapsed, stopping profiling for:', transaction.name || transaction.description);
    } // If the timeout exceeds, we want to stop profiling, but not finish the transaction


    void onProfileHandler();
  }, MAX_PROFILE_DURATION_MS); // We need to reference the original finish call to avoid creating an infinite loop

  var originalFinish = transaction.finish.bind(transaction);
  /**
   * Wraps startTransaction and stopTransaction with profiling related logic.
   * startProfiling is called after the call to startTransaction in order to avoid our own code from
   * being profiled. Because of that same reason, stopProfiling is called before the call to stopTransaction.
   */

  function profilingWrappedTransactionFinish() {
    if (!transaction) {
      return originalFinish();
    } // onProfileHandler should always return the same profile even if this is called multiple times.
    // Always call onProfileHandler to ensure stopProfiling is called and the timeout is cleared.


    void onProfileHandler().then(function () {
      transaction.setContext('profile', {
        profile_id: profileId,
        start_timestamp: startTimestamp
      });
      originalFinish();
    }, function () {
      // If onProfileHandler fails, we still want to call the original finish method.
      originalFinish();
    });
    return transaction;
  }

  transaction.finish = profilingWrappedTransactionFinish;
  return transaction;
}

export { onProfilingStartRouteTransaction, startProfileForTransaction };